import {debounce} from 'lodash';
import React, {useEffect, useMemo, useState} from 'react';

import {
  Button,
  Heading,
  Icon,
  IconButton,
  LoadingSpinner,
  SearchInput,
  SegmentedControl
} from '@albert-io/atomic';
import {SortDropdown} from 'client/TeacherDashboard/components/SortDropdown';
import type {
  ClassworkCreatedByFilter,
  SelectOption,
  SortValue
} from 'client/TeacherDashboard/types';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {history} from 'client/history';

import {AssignmentRow} from './AssignmentRow';
import {
  AssignmentsFilteredEmptyState,
  AssignmentsOnboardingEmptyState,
  AssignmentStatusEmptyState
} from './AssignmentsEmptyState';
import type {StatusSegment} from './constants';
import {CREATE_ASSIGNMENT_PATH, STATUS_SEGMENTS} from './constants';
import {FiltersMenu} from './FiltersMenu';
import {useAssignmentsScope} from './hooks/useAssignmentsScope';
import type {AssignmentSortValue} from './hooks/useTeacherAssignments';
import {useTeacherAssignments} from './hooks/useTeacherAssignments';

const SORT_OPTIONS: ReadonlyArray<SelectOption<AssignmentSortValue>> = [
  {value: 'recentlyEdited', label: 'Recently Edited'},
  {value: 'name', label: 'Name'}
];

const DEFAULT_SORT: SortValue<AssignmentSortValue> = {field: 'recentlyEdited', order: 'desc'};

const ListSpinner = () => (
  <LoadingSpinner className='p-x6' icon='loader' iconStyle='light' size={3} />
);

const getInitialSegment = (): StatusSegment => {
  const hash = window.location.hash.substring(1);
  // The legacy list linked here with #drafts.
  if (hash === 'drafts') {
    return 'draft';
  }
  const match = STATUS_SEGMENTS.find((s) => s.value === hash);
  return match ? match.value : 'open';
};

interface Props {
  /** Scopes the list to one class (the class detail page); omitted for the cross-class view. */
  classroomId?: string;
}

export const AssignmentsList = ({classroomId}: Props) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
  const [createdBy, setCreatedBy] = useState<ClassworkCreatedByFilter>('all');
  const [sort, setSort] = useState<SortValue<AssignmentSortValue>>(DEFAULT_SORT);
  const [segment, setSegment] = useState<StatusSegment>(getInitialSegment);

  const isDraftSegment = segment === 'draft';

  // Switching class scope (via the sidemenu dropdown) keeps you on the assignments
  // tab but resets filters + sort
  const [trackedClassroomId, setTrackedClassroomId] = useState(classroomId);
  if (trackedClassroomId !== classroomId) {
    setTrackedClassroomId(classroomId);
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setCreatedBy('all');
    setSort(DEFAULT_SORT);
  }

  const actorTeacherId = masqueradeStore.getUserIdByMasqueradeState() ?? '';
  const {assignments, isLoading, hasNextPage, isLoadingMore, loadMore, refetch} =
    useTeacherAssignments({
      search: debouncedSearchTerm,
      segment,
      createdBy,
      actorTeacherId,
      sort,
      classroomId
    });

  const debouncedSetSearch = useMemo(() => debounce(setDebouncedSearchTerm, 200), []);
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const handleSegmentChange = (value: string | number) => {
    if (value === segment) {
      return;
    }
    window.location.hash = String(value);
    setSegment(value as StatusSegment);
    // Drafts are actor-only; clear createdBy so a carried-over filter doesn't empty the list.
    if (value === 'draft') {
      setCreatedBy('all');
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    debouncedSetSearch.cancel();
    setDebouncedSearchTerm('');
  };

  const hasFiltersOn = createdBy !== 'all' || !!debouncedSearchTerm;
  const {isCheckingScope, isScopeEmpty} = useAssignmentsScope({
    assignmentCount: assignments.length,
    hasFiltersOn,
    classroomId
  });

  // Onboarding (whole scope empty)
  if (isScopeEmpty) {
    return <AssignmentsOnboardingEmptyState />;
  }

  return (
    <div className='flex flex-col gap-x3 pt-x4'>
      <Heading as='h2' className='m-x0' size='s'>
        Assignments
      </Heading>
      <div className='flex flex-wrap items-center justify-between gap-x2'>
        <SegmentedControl
          buttonClassName='font-normal'
          onSelect={handleSegmentChange}
          options={STATUS_SEGMENTS}
          selectedIdx={STATUS_SEGMENTS.findIndex((s) => s.value === segment)}
        />
        <div className='flex items-center gap-x1'>
          {/* Drafts have no applicable filters (no status/creator yet) — hide the Filters menu. */}
          {!isDraftSegment && <FiltersMenu createdBy={createdBy} onChange={setCreatedBy} />}
          <SortDropdown
            options={SORT_OPTIONS}
            triggerIcon={['far', 'arrow-down-short-wide']}
            triggerLabel='Sort'
            value={sort}
            onChange={setSort}
          />
          {isSearchOpen ? (
            // React's onBlur bubbles like focusout: collapse when focus leaves with no query.
            <div
              onBlur={(e) => {
                if (!searchTerm && !e.currentTarget.contains(e.relatedTarget as Node | null)) {
                  setIsSearchOpen(false);
                }
              }}
            >
              <SearchInput
                autoFocus
                className='w-[256px]'
                placeholder='Search'
                rectangle
                value={searchTerm}
                onChange={(_e, value) => {
                  setSearchTerm(value);
                  debouncedSetSearch(value);
                }}
                onReset={() => {
                  clearSearch();
                  setIsSearchOpen(false);
                }}
              />
            </div>
          ) : (
            <IconButton
              color='secondary'
              icon='magnifying-glass'
              label='Search assignments'
              roundness='square'
              variant='outlined'
              onClick={() => setIsSearchOpen(true)}
            />
          )}
          <Button onClick={() => history.pushState(null, CREATE_ASSIGNMENT_PATH)}>
            <Icon className='mr-x1' icon='file-plus' iconStyle='regular' />
            New
          </Button>
        </div>
      </div>
      {assignments.length > 0 ? (
        <>
          <ul className='m-x0 flex list-none flex-col p-x0'>
            {assignments.map((assignment) => (
              <li key={assignment.id}>
                <AssignmentRow
                  actorTeacherId={actorTeacherId}
                  assignment={assignment}
                  classroomId={classroomId}
                  status={segment}
                  onDeleteComplete={refetch}
                />
              </li>
            ))}
          </ul>
          {hasNextPage && (
            <div className='flex justify-center py-x3'>
              <Button disabled={isLoadingMore} onClick={loadMore}>
                Show more
                {isLoadingMore && <Icon className='ml-x1' icon='spinner' spin />}
              </Button>
            </div>
          )}
        </>
      ) : isCheckingScope || isLoading ? (
        <ListSpinner />
      ) : hasFiltersOn ? (
        <AssignmentsFilteredEmptyState />
      ) : (
        <AssignmentStatusEmptyState segment={segment} />
      )}
    </div>
  );
};
